var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCart, useUI } from "../../../providers";
import Decorator from "../../../functions/decorator";
import { ItemContext } from "./Item";
import { QuantitySelector } from "./QuantitySelector";
import { ItemGroupSection } from "./ItemGroupSection";
import { ItemStyles } from "./ItemStyles";
import { IncludedModifierSection } from "./IncludedModifierSection";
import { AvailableModifierGroups } from "./AvailableModifierGroups";
import { AddOnItems } from "./AddOnItems";
import { RadixTextArea } from "../../primitives/RadixTextArea";
import { ITEM_COMMENT_MAX_LENGTH, itemCommentSchema } from "./itemCommentSchema";
import { buildIncludedModifierNameSet, filterOptionalModifierGroups } from "./modifierVisibility";
import { shouldDisplayMenuEntity } from "../../utility/menuItemVisibility";
import "../itemCustomization.css";
var REQUIRED_SELECTIONS_ERROR_MESSAGE = "Please complete required selections before adding to cart.";
var HTML_TAG_WHITESPACE = " \t\r\n\f";
var isHtmlTagWhitespace = function (character) {
    return typeof character === "string" && HTML_TAG_WHITESPACE.indexOf(character) !== -1;
};
var isSupportedLineBreakTag = function (tagContents) {
    var _a, _b;
    var characterIndex = 0;
    while (isHtmlTagWhitespace(tagContents[characterIndex])) {
        characterIndex += 1;
    }
    if (((_a = tagContents[characterIndex]) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== "b" || ((_b = tagContents[characterIndex + 1]) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== "r") {
        return false;
    }
    characterIndex += 2;
    while (isHtmlTagWhitespace(tagContents[characterIndex])) {
        characterIndex += 1;
    }
    if (tagContents[characterIndex] === "/") {
        characterIndex += 1;
        while (isHtmlTagWhitespace(tagContents[characterIndex])) {
            characterIndex += 1;
        }
    }
    // Attributes and any other trailing content must remain escaped description text.
    return characterIndex === tagContents.length;
};
var splitDescriptionIntoLines = function (description) {
    var lines = [];
    var currentLineCharacters = [];
    var finishCurrentLine = function () {
        lines.push(currentLineCharacters.join(""));
        currentLineCharacters = [];
    };
    for (var characterIndex = 0; characterIndex < description.length; characterIndex += 1) {
        var character = description[characterIndex];
        if (character === "\r" || character === "\n") {
            finishCurrentLine();
            if (character === "\r" && description[characterIndex + 1] === "\n") {
                characterIndex += 1;
            }
            continue;
        }
        if (character === "<") {
            var closingBracketIndex = description.indexOf(">", characterIndex + 1);
            if (closingBracketIndex !== -1) {
                var tagContents = description.slice(characterIndex + 1, closingBracketIndex);
                if (isSupportedLineBreakTag(tagContents)) {
                    finishCurrentLine();
                    characterIndex = closingBracketIndex;
                    continue;
                }
            }
        }
        currentLineCharacters.push(character);
    }
    finishCurrentLine();
    return lines;
};
export var StreamlinedItemCustomization = function (_a) {
    var onAddSuccess = _a.onAddSuccess, _b = _a.editMode, editMode = _b === void 0 ? false : _b, submitCustomizationAttempt = _a.submitCustomizationAttempt, successMessage = _a.successMessage, _c = _a.showSuccessToast, showSuccessToast = _c === void 0 ? true : _c;
    var _d = useState(""), submitError = _d[0], setSubmitError = _d[1];
    var _e = useState([]), missingRequiredGroups = _e[0], setMissingRequiredGroups = _e[1];
    var showToast = useUI().showToast;
    var _f = useCart(), submitItemAttempt = _f.submitItemAttempt, submitEditItemAttempt = _f.submitEditItemAttempt, stopEditingItem = _f.stopEditingItem;
    var _g = useForm({
        resolver: zodResolver(itemCommentSchema),
        mode: "onChange",
        defaultValues: {
            comment: "",
        },
    }), itemCommentControl = _g.control, triggerItemCommentValidation = _g.trigger, itemCommentErrors = _g.formState.errors;
    return (React.createElement(ItemContext.Consumer, null, function (itemContext) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (!itemContext || itemContext.loading) {
            return React.createElement("div", { className: "container p-3" }, "Loading item...");
        }
        if (itemContext.hasServerError) {
            return React.createElement("div", { className: "container p-3" }, "We\u2019re having trouble loading this item right now. Please refresh the page and try again.");
        }
        if (itemContext.no_items) {
            return React.createElement("div", { className: "container p-3" }, "This item is not currently available.");
        }
        var subtotal = itemContext.functions.calculateSubtotal();
        var hasPriceError = !Number.isFinite(subtotal) || subtotal < 0;
        var evaluateMissingRequiredGroups = function (choices) {
            var styleGroups = itemContext.styles || [];
            return styleGroups
                .filter(function (styleGroup) {
                var groupModifiers = (styleGroup === null || styleGroup === void 0 ? void 0 : styleGroup.modifiers) || [];
                return !groupModifiers.some(function (styleModifierId) {
                    var _a;
                    return (((_a = choices[String(styleModifierId)]) === null || _a === void 0 ? void 0 : _a.quantity) || 0) > 0;
                });
            })
                .map(function (styleGroup) { return String(styleGroup.name); });
        };
        var handleStyleSelection = function (group, modifierId) {
            var _a;
            var groupModifiers = (group === null || group === void 0 ? void 0 : group.modifiers) || [];
            groupModifiers.forEach(function (id) {
                itemContext.functions.setModifierQuantity(String(id), String(id) === String(modifierId) ? 1 : 0);
            });
            var nextChoices = __assign({}, (((_a = itemContext.choices) === null || _a === void 0 ? void 0 : _a.modifiers) || {}));
            groupModifiers.forEach(function (id) {
                var key = String(id);
                nextChoices[key] = __assign(__assign({}, (nextChoices[key] || { portion: "whole", size: "normal", quantity: 0 })), { quantity: key === String(modifierId) ? 1 : 0 });
            });
            var unresolvedGroups = evaluateMissingRequiredGroups(nextChoices);
            setMissingRequiredGroups(unresolvedGroups);
            if (unresolvedGroups.length === 0) {
                if (submitError === REQUIRED_SELECTIONS_ERROR_MESSAGE) {
                    setSubmitError("");
                }
                itemContext.functions.markAsValid();
            }
            else {
                itemContext.functions.markAsInvalid();
            }
        };
        var scrollToRequiredGroup = function (groupName) {
            var requiredSections = Array.from(document.querySelectorAll("[data-required-group-name]"));
            var sectionElement = requiredSections.find(function (element) { return element.getAttribute("data-required-group-name") === groupName; });
            if (!sectionElement) {
                return;
            }
            var scrollContainer = sectionElement.closest(".item-customization-dialog-body");
            if (!scrollContainer) {
                sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
            var containerRect = scrollContainer.getBoundingClientRect();
            var sectionRect = sectionElement.getBoundingClientRect();
            var offsetTopWithinContainer = sectionRect.top - containerRect.top + scrollContainer.scrollTop;
            scrollContainer.scrollTo({
                top: Math.max(0, offsetTopWithinContainer - 12),
                behavior: "smooth"
            });
        };
        var includedIds = Object.keys(((_a = itemContext.choices) === null || _a === void 0 ? void 0 : _a.includedModifiers) || {});
        var excludedIds = (itemContext.functions.determineExcludedModifiers() || []).map(function (mod) { return String(mod); });
        var optionalGroups = (itemContext.options || []).filter(function (group) { return group.controlType === "checkbox"; });
        var includedModifierNames = buildIncludedModifierNameSet(includedIds, itemContext.modifiers || {});
        var filteredOptionalGroups = filterOptionalModifierGroups(optionalGroups, itemContext.modifiers || {}, includedModifierNames);
        var calculateSelectionWeight = function (choice) {
            if (!choice || choice.quantity <= 0) {
                return 0;
            }
            var portionWeight = choice.portion === "first-half" || choice.portion === "second-half" ? 0.5 : 1;
            var sizeWeight = choice.size === "extra" ? 2 : 1;
            return choice.quantity * portionWeight * sizeWeight;
        };
        var includedSelectedWeight = includedIds.reduce(function (runningTotal, modifierId) {
            var _a, _b;
            return runningTotal + calculateSelectionWeight((_b = (_a = itemContext.choices) === null || _a === void 0 ? void 0 : _a.includedModifiers) === null || _b === void 0 ? void 0 : _b[modifierId]);
        }, 0);
        var itemCommentsSetting = (_c = (_b = window.session) === null || _b === void 0 ? void 0 : _b.store) === null || _c === void 0 ? void 0 : _c.show_item_comments;
        var showItemComments = itemCommentsSetting === true || itemCommentsSetting === 1 || itemCommentsSetting === "1" || itemCommentsSetting === "true";
        var itemComment = typeof itemContext.comment === "string" ? itemContext.comment : "";
        var itemCommentCharactersRemaining = Math.max(0, ITEM_COMMENT_MAX_LENGTH - itemComment.length);
        var description = typeof itemContext.description === "string" ? itemContext.description : "";
        var descriptionLines = splitDescriptionIntoLines(description);
        var visibleMainItems = (((_d = itemContext.itemGroup) === null || _d === void 0 ? void 0 : _d.items) || [])
            .map(function (item, selectionIndex) { return (__assign(__assign({}, item), { selectionIndex: selectionIndex })); })
            .filter(function (item) { return shouldDisplayMenuEntity(item); });
        var handleSubmit = function () { return __awaiter(void 0, void 0, void 0, function () {
            var itemCommentIsValid, _a, unresolvedGroups, submitResult, _b, reason, mainItem, badModifier, reasons, firstMissingGroup;
            var _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (hasPriceError) {
                            return [2 /*return*/];
                        }
                        setSubmitError("");
                        if (!showItemComments) return [3 /*break*/, 2];
                        return [4 /*yield*/, triggerItemCommentValidation("comment")];
                    case 1:
                        _a = _d.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        _a = true;
                        _d.label = 3;
                    case 3:
                        itemCommentIsValid = _a;
                        if (!itemCommentIsValid) {
                            return [2 /*return*/];
                        }
                        unresolvedGroups = evaluateMissingRequiredGroups(((_c = itemContext.choices) === null || _c === void 0 ? void 0 : _c.modifiers) || {});
                        setMissingRequiredGroups(unresolvedGroups);
                        if (unresolvedGroups.length > 0) {
                            setSubmitError(REQUIRED_SELECTIONS_ERROR_MESSAGE);
                            scrollToRequiredGroup(unresolvedGroups[0]);
                            itemContext.functions.markAsInvalid();
                            return [2 /*return*/];
                        }
                        if (!submitCustomizationAttempt) return [3 /*break*/, 5];
                        return [4 /*yield*/, Promise.resolve(submitCustomizationAttempt(itemContext))];
                    case 4:
                        _b = _d.sent();
                        return [3 /*break*/, 7];
                    case 5: return [4 /*yield*/, (editMode ? submitEditItemAttempt(itemContext) : submitItemAttempt(itemContext))];
                    case 6:
                        _b = _d.sent();
                        _d.label = 7;
                    case 7:
                        submitResult = _b;
                        if (typeof submitResult === "undefined") {
                            if (showSuccessToast) {
                                showToast(successMessage || (editMode ? "Item updated in cart." : "Item added to cart."), "success");
                            }
                            if (onAddSuccess) {
                                onAddSuccess(itemContext.itemNavigationID);
                            }
                            if (editMode) {
                                stopEditingItem();
                            }
                            return [2 /*return*/];
                        }
                        reason = submitResult[0], mainItem = submitResult[1], badModifier = submitResult[2], reasons = submitResult[3];
                        if (reason === "invalid_selection") {
                            setSubmitError("".concat((badModifier === null || badModifier === void 0 ? void 0 : badModifier.name) || "A selection", " is not available with ").concat((mainItem === null || mainItem === void 0 ? void 0 : mainItem.name) || "this item", "."));
                            return [2 /*return*/];
                        }
                        if (reason === "requirements_unmet") {
                            setSubmitError(String(reasons || "Please complete required selections."));
                            firstMissingGroup = unresolvedGroups[0];
                            if (firstMissingGroup) {
                                scrollToRequiredGroup(firstMissingGroup);
                            }
                            return [2 /*return*/];
                        }
                        if (reason === "selection_not_allowed") {
                            setSubmitError("This selection combination is not allowed.");
                            return [2 /*return*/];
                        }
                        if (reason === "cart_add_failed") {
                            setSubmitError(String(reasons || "We couldn’t add this item to your cart. Please try again."));
                            return [2 /*return*/];
                        }
                        setSubmitError(String(reasons || "We couldn’t add this item to your cart. Please try again."));
                        return [2 /*return*/];
                }
            });
        }); };
        return (React.createElement("article", { className: "item-customization-layout" },
            React.createElement("header", { className: "item-customization-hero" },
                itemContext.imageSmall || itemContext.imageLarge ? (React.createElement("img", { src: itemContext.imageSmall || itemContext.imageLarge, alt: itemContext.name, className: "item-customization-hero-image" })) : (React.createElement("div", { className: "item-customization-hero-placeholder" }, "No image available")),
                React.createElement("div", { className: "item-customization-hero-content" },
                    React.createElement("h2", { className: "item-customization-title" }, itemContext.name),
                    React.createElement("p", { className: "item-customization-description" }, descriptionLines.map(function (line, lineIndex) { return (React.createElement(React.Fragment, { key: lineIndex },
                        lineIndex > 0 ? React.createElement("br", null) : null,
                        line)); })),
                    React.createElement(QuantitySelector, { quantity: itemContext.quantity, onDecrease: itemContext.functions.decreaseItemQuantity, onIncrease: itemContext.functions.increaseItemQuantity }))),
            React.createElement("div", { className: "item-customization-body" },
                React.createElement(ItemGroupSection, { title: ((_e = itemContext.itemGroup) === null || _e === void 0 ? void 0 : _e.displayName) || "Choose item", items: visibleMainItems, selectedIndex: ((_f = itemContext.choices) === null || _f === void 0 ? void 0 : _f.item) || 0, onSelect: itemContext.functions.selectMainItem }),
                React.createElement(ItemStyles, { styleGroups: itemContext.styles || [], modifiers: itemContext.modifiers || {}, choices: ((_g = itemContext.choices) === null || _g === void 0 ? void 0 : _g.modifiers) || {}, selectedMainItem: ((_j = (_h = itemContext.itemGroup) === null || _h === void 0 ? void 0 : _h.items) === null || _j === void 0 ? void 0 : _j[((_k = itemContext.choices) === null || _k === void 0 ? void 0 : _k.item) || 0]) || null, onSelectStyle: handleStyleSelection, missingRequiredGroupNames: missingRequiredGroups }),
                React.createElement(IncludedModifierSection, { includedModifierIds: includedIds, modifiers: itemContext.modifiers || {}, selectedMainItem: ((_m = (_l = itemContext.itemGroup) === null || _l === void 0 ? void 0 : _l.items) === null || _m === void 0 ? void 0 : _m[((_o = itemContext.choices) === null || _o === void 0 ? void 0 : _o.item) || 0]) || null, includedChoices: ((_p = itemContext.choices) === null || _p === void 0 ? void 0 : _p.includedModifiers) || {}, excludedModifierIds: excludedIds, onExclude: itemContext.functions.excludeIncludedItem, onReAdd: itemContext.functions.reAddIncludedItem, onSetFirstHalf: function (modifierId) { return itemContext.functions.setFirstHalfOnly(modifierId, true); }, onSetSecondHalf: function (modifierId) { return itemContext.functions.setSecondHalfOnly(modifierId, true); }, onSetWhole: function (modifierId) { return itemContext.functions.setWholePortion(modifierId, true); }, onSetExtra: function (modifierId) { return itemContext.functions.setExtra(modifierId, true); }, onSetNormal: function (modifierId) { return itemContext.functions.setNormalSize(modifierId, true); } }),
                React.createElement(AvailableModifierGroups, { groups: filteredOptionalGroups, modifiers: itemContext.modifiers || {}, choices: ((_q = itemContext.choices) === null || _q === void 0 ? void 0 : _q.modifiers) || {}, includedSelectedWeight: includedSelectedWeight, maxToppings: Number(itemContext.maxToppings) || 0, onSetFirstHalf: itemContext.functions.setFirstHalfOnly, onSetSecondHalf: itemContext.functions.setSecondHalfOnly, onSetWhole: itemContext.functions.setWholePortion, onSetExtra: itemContext.functions.setExtra, onSetNormal: itemContext.functions.setNormalSize, onRemove: itemContext.functions.removeOptionalModifier }),
                React.createElement(AddOnItems, { addonGroups: itemContext.addonGroups || [], addOnChoices: ((_r = itemContext.choices) === null || _r === void 0 ? void 0 : _r.addOns) || {}, onChoose: itemContext.functions.chooseAddOnItem, onIncrease: itemContext.functions.increaseAddOnQuantity, onDecrease: itemContext.functions.decreaseAddOnQuantity, onRemove: itemContext.functions.removeAddOnItem }),
                showItemComments ? (React.createElement("section", { className: "item-customization-section", "aria-labelledby": "item-customization-comment-title" },
                    React.createElement("div", { className: "item-customization-section-header" },
                        React.createElement("h3", { id: "item-customization-comment-title", className: "item-customization-section-title" }, "Comment"),
                        React.createElement("span", { className: "item-customization-comment-count" },
                            itemCommentCharactersRemaining,
                            " characters left")),
                    React.createElement(Controller, { control: itemCommentControl, name: "comment", defaultValue: itemComment, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (React.createElement(RadixTextArea, { className: "item-customization-comment-input", name: field.name, id: "itemComment", rows: 4, maxLength: ITEM_COMMENT_MAX_LENGTH, placeholder: "Item comments may result in extra charges.", value: itemComment !== null && itemComment !== void 0 ? itemComment : field.value, onBlur: field.onBlur, onChange: function (event) {
                                    field.onChange(event);
                                    itemContext.functions.handleChangeToItemComment(event);
                                }, errorMessage: (_b = itemCommentErrors.comment) === null || _b === void 0 ? void 0 : _b.message }));
                        } }))) : null),
            React.createElement("footer", { className: "item-customization-footer" },
                submitError ? React.createElement("div", { className: "item-customization-submit-error" }, submitError) : null,
                React.createElement("button", { type: "button", className: "item-customization-submit", onClick: handleSubmit, disabled: hasPriceError },
                    React.createElement("span", null, editMode ? "Save changes" : "Add to cart"),
                    React.createElement("span", null, hasPriceError ? "ERROR" : Decorator.formatCurrency(subtotal))))));
    }));
};
